import React, { useState } from 'react';
import { styled } from '@mui/material/styles';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Grid, Typography } from '@mui/material';
import { AutoGenHelperParams, JobType } from '../../types/types';

export const COLUMN_IS_MISSING = 'columnIsMissing';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  color: theme.palette.secondary.contrastText,
}));

interface AutoGenHelperProps {
  autogenHelper: AutoGenHelperParams;
}

const FileSourceMapping: Record<string, Record<JobType, string>> = {
  'Pts Shp': {
    Zone: 'core identifiers (1.1, 1-1, etc)',
    Grid: 'sample identifiers (1, 2, 3, etc)',
    Modgrid: 'sample identifiers (1, 2, 3, etc)',
  },
  'Sample Zones Shp': {
    Zone: 'zone numbers (1, 2, 3, etc or A, B, C, etc)',
    Grid: '',
    Modgrid: '',
  },
};

export default function AutoGenHelper(props: AutoGenHelperProps) {
  const { title, selection, headers, data, featureTypes, onSelection } = props.autogenHelper;
  let displayData: string[][] = [];
  let displayHeaders: string[] = [];

  if (props.autogenHelper.autoFilter || true) {
    for (const _ of data) {
      displayData.push([]);
    }

    for (const [columnIndex, columnHeader] of headers.entries()) {
      const rowHasValues = data.some((row) => !!row[columnIndex]);

      if (rowHasValues) {
        displayData.forEach((_, rowIndex) => displayData[rowIndex].push(data[rowIndex][columnIndex]));
        displayHeaders.push(columnHeader);
      }
    }
  } else {
    displayData = data;
    displayHeaders = headers;
  }

  const [value, setValue] = useState<string>();
  const selectedCol = value || selection;
  const helperText = FileSourceMapping[props.autogenHelper.source][props.autogenHelper.jobType];
  const sanitizedSource = props.autogenHelper.source
    .replace('Pts', 'Points')
    .replace('Shp', 'Shapefile')
    .replace('Bnd', 'Boundary');

  return (
    <Dialog disableEscapeKeyDown maxWidth={false} open={true} style={{ zIndex: 1600 }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent style={{ minHeight: 70 }}>
        <Grid container direction="row" size={12}>
          <Grid size={4}>
            <Typography variant="body1">
              {Object.entries(featureTypes)
                .map((entry) => `${entry[0]}: ${entry[1]}`)
                .join(', ')}
            </Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="body1">{`File Source: ${sanitizedSource}`}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="body1">{helperText ? `Look for ${helperText}` : ``}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogContent>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {displayHeaders.map((header) =>
                  header === selectedCol ? (
                    <StyledTableCell
                      key={header}
                      data-testid={`auto-gen-${header}`}
                      onClick={() => {
                        setValue(header);
                      }}
                    >
                      {header}
                    </StyledTableCell>
                  ) : (
                    <TableCell
                      key={header}
                      data-testid={`auto-gen-${header}`}
                      onClick={() => {
                        setValue(header);
                      }}
                    >
                      {header}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.map((row) => (
                <TableRow key={JSON.stringify(row)}>
                  {[...row.entries()].map((entry) =>
                    displayHeaders[entry[0]] === selectedCol ? (
                      <StyledTableCell
                        key={`${entry[0]}_${entry[1]}`}
                        onClick={() => {
                          setValue(displayHeaders[entry[0]]);
                        }}
                      >
                        {entry[1]}
                      </StyledTableCell>
                    ) : (
                      <TableCell
                        key={`${entry[0]}_${entry[1]}`}
                        onClick={() => {
                          setValue(displayHeaders[entry[0]]);
                        }}
                      >
                        {entry[1]}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="auto-gen-select"
          variant="contained"
          color="secondary"
          onClick={() => {
            onSelection(selectedCol);
          }}
        >
          Select
        </Button>

        <Button
          data-testid="auto-gen-ignore"
          variant="contained"
          onClick={() => {
            onSelection('ignore');
          }}
        >
          Ignore file
        </Button>

        <Button
          data-testid="auto-gen-column-missing"
          variant="contained"
          onClick={() => {
            onSelection(COLUMN_IS_MISSING);
          }}
        >
          Column is missing
        </Button>
      </DialogActions>
    </Dialog>
  );
}
